import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Logo } from "@/components/common";
import OtpStep from "@/components/OtpStep";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

function PasswordInput({ value, onChange, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} required data-testid={testId} autoComplete={autoComplete} className="h-11 pr-10" />
      <Button type="button" variant="ghost" size="icon" className="absolute right-0.5 top-0.5 h-10 w-10" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} data-testid={`${testId}-toggle`}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  );
}

function AuthLayout({ children, admin }) {
  return (
    <div className={admin ? "flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10" : "hero-bg flex min-h-screen items-center justify-center px-4 py-10"}>
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 inline-block" data-testid="auth-logo-link"><Logo dark={admin} /></Link>
        {children}
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, verifyOtp } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [challenge, setChallenge] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(sp.get("expired") ? "Your session expired. Please sign in again." : "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await login(email, password);
      if (r.challenge) setChallenge(r.challenge);
      else nav(sp.get("next") || "/dashboard");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <Card className="shadow-[0_10px_30px_hsl(222_35%_12%/0.10)]">
        <CardContent className="p-6 sm:p-8">
          {challenge ? (
            <OtpStep challenge={challenge} setChallenge={setChallenge} testPrefix="login-otp" title="Two-step verification" onBack={() => setChallenge(null)}
              onVerify={async (c) => { await verifyOtp(challenge.challenge_id, c, "user"); nav(sp.get("next") || "/dashboard"); }} />
          ) : (<>
          <h1 className="text-2xl font-semibold">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to manage your INR and USDT wallets.</p>
          {err && <Alert variant="destructive" className="mt-4" data-testid="login-error"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" data-testid="login-email-input" autoComplete="email" /></div>
            <div className="space-y-1.5"><Label>Password</Label><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} testId="login-password-input" autoComplete="current-password" /></div>
            <Button type="submit" className="btn-lift h-11 w-full" disabled={busy} data-testid="login-form-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign in</Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">New here? <Link to="/register" className="font-medium text-primary" data-testid="login-register-link">Create an account</Link></p>
          <p className="mt-1 text-sm text-muted-foreground"><Link to="/forgot-password" className="font-medium text-primary" data-testid="login-forgot-password-link">Forgot your password?</Link></p>
          </>)}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { register, verifyOtp } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [challenge, setChallenge] = useState(null);
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", referral_code: sp.get("ref") || "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return setErr("Password must be at least 8 characters");
    if (f.phone.replace(/\D/g, "").length < 10) return setErr("Enter your 10-digit mobile number");
    setBusy(true);
    setErr("");
    try {
      const r = await register({ ...f, referral_code: f.referral_code.trim() || null });
      if (r.challenge) setChallenge(r.challenge);
      else nav("/dashboard");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <Card className="shadow-[0_10px_30px_hsl(222_35%_12%/0.10)]">
        <CardContent className="p-6 sm:p-8">
          {challenge ? (
            <OtpStep challenge={challenge} setChallenge={setChallenge} testPrefix="register-otp" title="Verify your email"
              onVerify={async (c) => { await verifyOtp(challenge.challenge_id, c, "user"); nav("/dashboard"); }} />
          ) : (<>
          <h1 className="text-2xl font-semibold">Create your account</h1>
          <p className="mt-1 text-sm text-muted-foreground">INR and USDT wallets are created instantly.</p>
          {err && <Alert variant="destructive" className="mt-4" data-testid="register-error"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={set("name")} required minLength={2} className="h-11" data-testid="register-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} required className="h-11" data-testid="register-email-input" /></div>
            <div className="space-y-1.5"><Label>Mobile number</Label><div className="flex"><span className="flex h-11 items-center rounded-l-md border border-r-0 bg-secondary px-3 text-sm text-muted-foreground">+91</span><Input type="tel" inputMode="numeric" value={f.phone} onChange={set("phone")} required placeholder="9876543210" className="h-11 rounded-l-none" data-testid="register-phone-input" /></div></div>
            <div className="space-y-1.5"><Label>Password</Label><PasswordInput value={f.password} onChange={set("password")} testId="register-password-input" autoComplete="new-password" /><p className="text-xs text-muted-foreground">At least 8 characters.</p></div>
            <div className="space-y-1.5"><Label>Referral code <span className="text-muted-foreground">(optional)</span></Label><Input value={f.referral_code} onChange={(e) => setF({ ...f, referral_code: e.target.value.toUpperCase() })} placeholder="RTXXXXXX" className="num h-11" data-testid="register-referral-input" /></div>
            <Button type="submit" className="btn-lift h-11 w-full" disabled={busy} data-testid="register-form-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create account</Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">Already registered? <Link to="/login" className="font-medium text-primary" data-testid="register-login-link">Sign in</Link></p>
          </>)}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}

export function AdminLoginPage() {
  const { adminLogin, verifyOtp } = useAuth();
  const nav = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(sp.get("expired") ? "Session expired. Sign in again." : "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await adminLogin(email, password);
      if (r.challenge) setChallenge(r.challenge);
      else nav("/admin");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout admin>
      <Card className="border-slate-800">
        <CardContent className="p-6 sm:p-8">
          {challenge ? (
            <OtpStep challenge={challenge} setChallenge={setChallenge} testPrefix="admin-otp" title="Admin verification" onBack={() => setChallenge(null)}
              onVerify={async (c) => { await verifyOtp(challenge.challenge_id, c, "admin"); nav("/admin"); }} />
          ) : (<>
          <p className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-[hsl(var(--accent))]"><ShieldCheck className="h-3.5 w-3.5" /> Admin</p>
          <h1 className="mt-4 text-2xl font-semibold">Administrator sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Restricted area. Every sign-in and action is recorded in the audit log.</p>
          {err && <Alert variant="destructive" className="mt-4" data-testid="admin-login-error"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5"><Label>Admin email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" data-testid="admin-login-email-input" /></div>
            <div className="space-y-1.5"><Label>Password</Label><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} testId="admin-login-password-input" autoComplete="current-password" /></div>
            <Button type="submit" className="h-11 w-full bg-slate-900 hover:bg-slate-800" disabled={busy} data-testid="admin-login-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign in to console</Button>
          </form>
          </>)}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
